import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ActivityIcon as Activity,
  CircleNotchIcon as Spinner,
  CaretDownIcon as CaretDown,
  CaretRightIcon as CaretRight,
  FolderPlusIcon as FolderPlus,
  ChatCircleTextIcon as ChatCircle,
  FileTextIcon as FileText,
  EyeIcon as Eye,
  UserPlusIcon as UserPlus,
  UserMinusIcon as UserMinus,
  EnvelopeSimpleIcon as Envelope,
  EnvelopeOpenIcon as EnvelopeOpen,
  CrownIcon as Crown,
  PencilSimpleIcon as Pencil,
  ShareNetworkIcon as ShareNetwork,
  PaperPlaneTiltIcon as PaperPlane,
  CircleIcon as Circle,
  SealCheckIcon as SealCheck,
  TreeStructureIcon as TreeStructure,
} from "@phosphor-icons/react";
import { getActivityFeed } from "@/lib/api";

// Relative-time formatter — duplicated from Workspaces.jsx; small enough that
// hoisting to a shared util doesn't pay for itself yet.
function formatRelative(iso) {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  const diffMs = Date.now() - then;
  const m = Math.round(diffMs / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.round(h / 24);
  if (d < 30) return `${d}d ago`;
  return new Date(iso).toLocaleDateString();
}

// Per-event rendering table — keeps the JSX flat and easy to grep.
const RENDERERS = {
  workspace_created: (e) => ({
    Icon: FolderPlus, color: "#0f62fe",
    text: <>created the workspace <strong>{e.workspace_name}</strong></>,
  }),
  session_started: (e) => ({
    Icon: ChatCircle, color: "#0f62fe",
    text: <>started a session{e.metadata?.title ? <> <strong>&ldquo;{e.metadata.title}&rdquo;</strong></> : ""} in <strong>{e.workspace_name}</strong></>,
  }),
  deliverable_generated: (e) => ({
    Icon: FileText, color: "#0e6027",
    text: <>generated a deliverable{e.metadata?.title ? <> <strong>&ldquo;{e.metadata.title}&rdquo;</strong></> : ""} in <strong>{e.workspace_name}</strong></>,
  }),
  briefing_generated: (e) => ({
    Icon: FileText, color: "#0e6027",
    text: <>generated a client briefing for <strong>{e.workspace_name}</strong></>,
  }),
  share_link_viewed: (e) => ({
    Icon: Eye, color: "#6929c4",
    text: <>client opened the share link for <strong>{e.workspace_name}</strong></>,
    anonymous: true,
  }),
  share_link_unfurled: (e) => ({
    Icon: ShareNetwork, color: "#525252",
    text: <>share link for <strong>{e.workspace_name}</strong> was unfurled on social</>,
    anonymous: true,
  }),
  share_link_copied: (e) => ({
    Icon: PaperPlane, color: "#525252",
    text: <>copied the share link for <strong>{e.workspace_name}</strong></>,
  }),
  email_opened_in_client: (e) => ({
    Icon: Envelope, color: "#525252",
    text: <>opened the email composer for <strong>{e.workspace_name}</strong></>,
  }),
  email_copied: (e) => ({
    Icon: Envelope, color: "#525252",
    text: <>copied the email body for <strong>{e.workspace_name}</strong></>,
  }),
  combined_pdf_exported: (e) => ({
    Icon: FileText, color: "#525252",
    text: <>exported the combined PDF for <strong>{e.workspace_name}</strong></>,
  }),
  single_deliverable_pdf_exported: (e) => ({
    Icon: FileText, color: "#525252",
    text: <>exported a deliverable PDF from <strong>{e.workspace_name}</strong></>,
  }),
  member_added: (e) => ({
    Icon: UserPlus, color: "#0e6027",
    text: <>added <strong>{e.metadata?.member_email || "a member"}</strong> as {e.metadata?.role || "member"} to <strong>{e.workspace_name}</strong></>,
  }),
  member_removed: (e) => ({
    Icon: UserMinus, color: "#a2191f",
    text: <>removed a member from <strong>{e.workspace_name}</strong></>,
  }),
  member_role_changed: (e) => ({
    Icon: e.metadata?.new_role === "owner" ? Crown : Pencil,
    color: "#6929c4",
    text: <>changed a member&apos;s role to <strong>{e.metadata?.new_role}</strong> in <strong>{e.workspace_name}</strong></>,
  }),
  invite_sent: (e) => ({
    Icon: Envelope, color: "#0f62fe",
    text: <>invited <strong>{e.metadata?.email || "someone"}</strong> as {e.metadata?.role || "member"} to <strong>{e.workspace_name}</strong></>,
  }),
  invite_accepted: (e) => ({
    Icon: EnvelopeOpen, color: "#0e6027",
    text: <><strong>{e.metadata?.email || "Someone"}</strong> accepted an invitation to <strong>{e.workspace_name}</strong></>,
  }),
  invite_revoked: (e) => ({
    Icon: UserMinus, color: "#a2191f",
    text: <>revoked the invitation for <strong>{e.metadata?.email || "someone"}</strong> in <strong>{e.workspace_name}</strong></>,
  }),
  business_rule_decided: (e) => ({
    Icon: SealCheck, color: e.metadata?.decision === "validated" ? "#0e6027" : "#525252",
    text: <>{e.metadata?.decision === "proposed" ? "reopened" : e.metadata?.decision || "reviewed"} <strong>{e.metadata?.count || 1}</strong> business rule{(e.metadata?.count || 1) === 1 ? "" : "s"} in <strong>{e.workspace_name}</strong></>,
  }),
  deliverable_decided: (e) => ({
    Icon: SealCheck, color: e.metadata?.decision === "validated" ? "#0e6027" : "#525252",
    text: <>{e.metadata?.decision === "ai_inferred" ? "reopened" : e.metadata?.decision || "reviewed"} the deliverable{e.metadata?.title ? <> <strong>&ldquo;{e.metadata.title}&rdquo;</strong></> : ""} in <strong>{e.workspace_name}</strong></>,
  }),
  field_references_rebuilt: (e) => ({
    Icon: TreeStructure, color: "#0f62fe",
    text: <>rebuilt field dependencies in <strong>{e.workspace_name}</strong> — {e.metadata?.total ?? 0} reference{(e.metadata?.total ?? 0) === 1 ? "" : "s"}{e.metadata?.ambiguous ? `, ${e.metadata.ambiguous} ambiguous` : ""}</>,
  }),
  screen_walkthrough_rebuilt: (e) => ({
    Icon: TreeStructure, color: "#0f62fe",
    text: <>rebuilt the screen walkthrough in <strong>{e.workspace_name}</strong> — {e.metadata?.total ?? 0} screen statement{(e.metadata?.total ?? 0) === 1 ? "" : "s"} across {e.metadata?.programs ?? 0} program{(e.metadata?.programs ?? 0) === 1 ? "" : "s"}</>,
  }),
  screen_format_bindings_rebuilt: (e) => ({
    Icon: TreeStructure, color: "#0f62fe",
    text: <>rebuilt screen format bindings in <strong>{e.workspace_name}</strong> — {e.metadata?.total ?? 0} binding{(e.metadata?.total ?? 0) === 1 ? "" : "s"}, {e.metadata?.bound ?? 0} bound{e.metadata?.manual_active ? `, ${e.metadata.manual_active} manual kept` : ""}</>,
  }),
  manual_screen_binding_created: (e) => ({
    Icon: TreeStructure, color: "#a2191f",
    text: <>added manual screen binding <strong>{e.metadata?.program_name} {String(e.metadata?.operation || "").toUpperCase()} {e.metadata?.display_file}/{e.metadata?.record_format}</strong> in <strong>{e.workspace_name}</strong> — consultant supplied, not parser-proven{e.metadata?.note ? <> · “{e.metadata.note}”</> : null}</>,
  }),
  manual_screen_binding_updated: (e) => ({
    Icon: TreeStructure, color: "#a2191f",
    text: <>updated manual screen binding <strong>{e.metadata?.program_name} {String(e.metadata?.operation || "").toUpperCase()} {e.metadata?.display_file}/{e.metadata?.record_format}</strong> in <strong>{e.workspace_name}</strong>{e.metadata?.changes ? <> · {Object.keys(e.metadata.changes).join(", ")}</> : null}</>,
  }),
  manual_screen_binding_deactivated: (e) => ({
    Icon: TreeStructure, color: "#a2191f",
    text: <>deactivated manual screen binding <strong>{e.metadata?.program_name} {String(e.metadata?.operation || "").toUpperCase()} {e.metadata?.display_file}/{e.metadata?.record_format}</strong> in <strong>{e.workspace_name}</strong></>,
  }),
  disposition_bulk_validated: (e) => bulkDispositionEvent(e, "validated", "#0e6027"),
  disposition_bulk_rejected: (e) => bulkDispositionEvent(e, "rejected", "#a2191f"),
  disposition_bulk_reopened: (e) => bulkDispositionEvent(e, "reopened", "#525252"),
};

function bulkDispositionEvent(e, verb, color) {
  const n = e.metadata?.affected_count ?? 0;
  const sel = e.metadata?.selection || {};
  const where = sel.type === "matrix_cell"
    ? `${sel.technical_treatment} × ${sel.migration_disposition}`
    : "a filtered selection";
  return {
    Icon: SealCheck, color,
    text: <>bulk-{verb} <strong>{n}</strong> disposition{n === 1 ? "" : "s"} in <strong>{where}</strong> of <strong>{e.workspace_name}</strong>{e.metadata?.excluded_count ? ` · ${e.metadata.excluded_count} excluded` : ""}{e.metadata?.note ? <> · “{e.metadata.note}”</> : null}</>,
  };
}

function eventRender(e) {
  const fn = RENDERERS[e.type];
  if (fn) return fn(e);
  return {
    Icon: Circle, color: "#8d8d8d",
    text: <>did <em>{e.type}</em> in <strong>{e.workspace_name || "a workspace"}</strong></>,
  };
}

const COLLAPSED_LIMIT = 5;

export default function ActivityFeed({ defaultOpen = false }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(defaultOpen);
  const [showAll, setShowAll] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getActivityFeed({ limit: 25 });
      setEvents(data.events || []);
    } catch (_e) {
      // Activity feed is non-critical — silent failure (most likely just no
      // events yet for a brand-new user). Errors are visible in devtools.
      setEvents([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    // Light polling — every 60s — only when the panel is expanded.
    if (!expanded) return undefined;
    const iv = setInterval(load, 60_000);
    return () => clearInterval(iv);
  }, [load, expanded]);

  const displayed = showAll ? events : events.slice(0, COLLAPSED_LIMIT);
  const hasMore = events.length > COLLAPSED_LIMIT;

  return (
    <div className="ml-card mb-6" data-testid="activity-feed">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        data-testid="activity-feed-toggle"
        className="w-full px-5 py-3 flex items-center gap-3 hover:bg-[var(--ml-surface)]/40 transition-colors text-left"
      >
        {expanded ? (
          <CaretDown size={14} weight="bold" className="text-[var(--ml-text-secondary)]" />
        ) : (
          <CaretRight size={14} weight="bold" className="text-[var(--ml-text-secondary)]" />
        )}
        <Activity size={16} weight="bold" className="text-[var(--ml-primary)]" />
        <span className="ml-mono text-[11px] uppercase tracking-widest font-semibold">
          Recent activity
        </span>
        <span
          className="ml-mono text-[10px] px-2 py-0.5 bg-[var(--ml-surface)] border border-[var(--ml-border)] text-[var(--ml-text-secondary)]"
          data-testid="activity-feed-count"
        >
          {loading ? "…" : events.length}
        </span>
      </button>

      {expanded && (
        <div className="border-t border-[var(--ml-border)]" data-testid="activity-feed-body">
          {loading ? (
            <div className="px-5 py-6 text-sm text-[var(--ml-text-secondary)] flex items-center gap-2" data-testid="activity-feed-loading">
              <Spinner size={14} className="animate-spin" />
              Loading…
            </div>
          ) : events.length === 0 ? (
            <div className="px-5 py-6 text-sm text-[var(--ml-text-secondary)]" data-testid="activity-feed-empty">
              No recent activity yet. Start a session or invite a teammate to populate this feed.
            </div>
          ) : (
            <>
              <ul className="divide-y divide-[var(--ml-border)]" data-testid="activity-feed-list">
                {displayed.map((e) => {
                  const r = eventRender(e);
                  const RIcon = r.Icon;
                  const actorLabel = r.anonymous
                    ? "A visitor"
                    : (e.actor_name || "Someone");
                  return (
                    <li
                      key={e.id}
                      className="px-5 py-3 flex items-start gap-3"
                      data-testid={`activity-row-${e.id}`}
                    >
                      <div
                        className="w-7 h-7 shrink-0 flex items-center justify-center"
                        style={{ background: `${r.color}14`, color: r.color }}
                      >
                        <RIcon size={14} weight="bold" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm leading-relaxed">
                          <strong className="text-[var(--ml-text-primary)]">{actorLabel}</strong>{" "}
                          <span className="text-[var(--ml-text-primary)]">{r.text}</span>
                        </div>
                        <div className="text-[11px] text-[var(--ml-text-secondary)] mt-0.5 flex items-center gap-2">
                          <span data-testid={`activity-time-${e.id}`}>{formatRelative(e.at)}</span>
                          {e.workspace_id && (
                            <>
                              <span>·</span>
                              <Link
                                to={`/workspaces/${e.workspace_id}`}
                                className="text-[var(--ml-primary)] hover:underline"
                                data-testid={`activity-link-${e.id}`}
                              >
                                Open workspace
                              </Link>
                            </>
                          )}
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
              {hasMore && (
                <button
                  type="button"
                  onClick={() => setShowAll((v) => !v)}
                  data-testid="activity-feed-show-all"
                  className="w-full px-5 py-3 ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-primary)] hover:bg-[var(--ml-surface)]/40 transition-colors border-t border-[var(--ml-border)]"
                >
                  {showAll
                    ? `Collapse (showing ${events.length})`
                    : `Show ${events.length - COLLAPSED_LIMIT} more`}
                </button>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
